import type { VideoStateManager } from '.';
import type { CallableToolArgsMap } from './callable-tools';
import type { SubagentScope } from './subagents/SubagentScope';
import type { TurnEdits } from './turn-edits/TurnEdits';
import { SKILL_NAMES } from './skills/skillNames';
import {
    DEFAULT_TOOLBOX_MODEL_VALUE as DEFAULT_TOOLBOX_MODEL_VALUE_STRING,
    DEFAULT_TOOLBOX_REASONING_EFFORT as DEFAULT_TOOLBOX_REASONING_EFFORT_STRING,
} from './toolbox-defaults';

export type ExecuteToolFn = <T extends keyof CallableToolArgsMap>(
    toolName: T,
    params: CallableToolArgsMap[T],
    assistantMessage?: string,
) => Promise<void>;

export interface Usage {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
    latency?: number; // in milliseconds
    // Prompt-caching counters, separate from input_tokens (which stays the
    // uncached remainder). Named after the Anthropic fields, but populated for
    // OpenAI too: the normalizers in LLMClient map `cached_tokens` and
    // `cache_write_tokens` onto these. Undefined for models without caching.
    cache_creation_input_tokens?: number;
    cache_read_input_tokens?: number;
    // The portion of cache_creation_input_tokens (NOT additional to it) written
    // at Anthropic's 1-hour TTL, which bills at 2x input instead of 1.25x.
    // Anthropic-only; absent when the provider reports no TTL breakdown.
    cache_creation_1h_input_tokens?: number;
    // The upstream OpenRouter routed this call to (e.g. "Together"). Only set for
    // OpenRouter-served models; absent for every other vendor.
    provider?: string;
}

export const emptyUsage = (): Usage => ({
    input_tokens: 0,
    output_tokens: 0,
    total_tokens: 0,
    cache_creation_input_tokens: 0,
    cache_read_input_tokens: 0,
});

/**
 * Adds `usage` into `total`, in place. Latency is left to the caller: it means model latency, and
 * a tool message carries an execution latency with no tokens behind it.
 */
export function addUsageInto(total: Usage, usage?: Usage): void {
    if (!usage) {
        return;
    }
    total.input_tokens += usage.input_tokens;
    total.output_tokens += usage.output_tokens;
    total.total_tokens += usage.total_tokens;
    if (usage.cache_creation_input_tokens || usage.cache_read_input_tokens) {
        total.cache_creation_input_tokens =
            (total.cache_creation_input_tokens || 0) + (usage.cache_creation_input_tokens || 0);
        total.cache_read_input_tokens =
            (total.cache_read_input_tokens || 0) + (usage.cache_read_input_tokens || 0);
    }
    if (usage.cache_creation_1h_input_tokens) {
        total.cache_creation_1h_input_tokens =
            (total.cache_creation_1h_input_tokens || 0) + usage.cache_creation_1h_input_tokens;
    }
}

export interface ModelOption {
    value: Model;
    label: string;
}
export enum Model {
    GPT_4_1_MINI = 'gpt-4.1-mini-2025-04-14',
    GPT_4_1_NANO = 'gpt-4.1-nano-2025-04-14',
    GPT_4_1 = 'gpt-4.1',
    GPT_4O = 'gpt-4o',
    GPT_4O_MINI = 'gpt-4o-mini',
    GPT_OSS_20B = 'openai/gpt-oss-20b',
    GPT_OSS_120B = 'openai/gpt-oss-120b',
    GPT_6_1_SOL = 'gpt-6.1-sol',
    GPT_6_SOL = 'gpt-6-sol',
    GPT_6_LUNA = 'gpt-6-luna',
    GPT_5_6_SOL = 'gpt-5.6-sol',
    GPT_5_6_TERRA = 'gpt-5.6-terra',
    GPT_5_6_LUNA = 'gpt-5.6-luna',
    GPT_5_5 = 'gpt-5.5-2026-04-23',
    GPT_5_4 = 'gpt-5.4-2026-03-05',
    GPT_5_4_MINI = 'gpt-5.4-mini-2026-03-17',
    GPT_5_4_NANO = 'gpt-5.4-nano-2026-03-17',
    GPT_5_2 = 'gpt-5.2-2025-12-11',
    GPT_5_1 = 'gpt-5.1-2025-11-13',
    GEMINI_3_FLASH_PREVIEW = 'google/gemini-3-flash-preview',
    GEMINI_3_5_FLASH = 'google/gemini-3.5-flash',
    GEMINI_3_5_FLASH_LITE = 'google/gemini-3.5-flash-lite',
    GEMINI_3_8_FLASH = 'google/gemini-3.8-flash',
    INKLING_SMALL = 'thinkingmachines/inkling-small',
    QWEN_3_7_PLUS = 'qwen/qwen3.7-plus',
    GROK_4_6 = 'x-ai/grok-4.6',
    KIMI_K2_6 = 'moonshotai/kimi-k2.6',
    DEEPSEEK_V4_1_FLASH = 'deepseek/deepseek-v4.1-flash',
    DEEPSEEK_V4_1_FLASH_1017 = 'deepseek/deepseek-v4.1-flash-1017',
    CLAUDE_FABLE_5 = 'claude-fable-5@default',
    CLAUDE_OPUS_5_5 = 'claude-opus-5-5@default',
    CLAUDE_OPUS_4_8 = 'claude-opus-4-8@default',
    CLAUDE_SONNET_5_5 = 'claude-sonnet-5-5@default',
    CLAUDE_SONNET_5 = 'claude-sonnet-5@default',
    CLAUDE_SONNET_4_6 = 'claude-sonnet-4-6@default',
    CLAUDE_HAIKU_4_5 = 'claude-haiku-4-5@20251001',
}

export const AVAILABLE_MODELS: ModelOption[] = [
    // GPT 6 family (newest)
    {
        value: Model.GPT_6_1_SOL,
        label: 'GPT 6.1 Sol',
    },
    {
        value: Model.GPT_6_SOL,
        label: 'GPT 6 Sol',
    },
    {
        value: Model.GPT_6_LUNA,
        label: 'GPT 6 Luna',
    },
    // GPT 5.x family
    {
        value: Model.GPT_5_6_SOL,
        label: 'GPT 5.6 Sol',
    },
    {
        value: Model.GPT_5_6_TERRA,
        label: 'GPT 5.6 Terra',
    },
    {
        value: Model.GPT_5_6_LUNA,
        label: 'GPT 5.6 Luna',
    },
    {
        value: Model.GPT_5_5,
        label: 'GPT 5.5',
    },
    {
        value: Model.GPT_5_4,
        label: 'GPT 5.4',
    },
    {
        value: Model.GPT_5_4_MINI,
        label: 'GPT 5.4 mini',
    },
    {
        value: Model.GPT_5_4_NANO,
        label: 'GPT 5.4 nano',
    },
    {
        value: Model.GPT_5_2,
        label: 'GPT 5.2',
    },
    {
        value: Model.GPT_5_1,
        label: 'GPT 5.1',
    },
    // GPT 4.x family
    {
        value: Model.GPT_4_1,
        label: 'GPT 4.1',
    },
    {
        value: Model.GPT_4_1_MINI,
        label: 'GPT 4.1 mini',
    },
    {
        value: Model.GPT_4_1_NANO,
        label: 'GPT 4.1 nano',
    },
    {
        value: Model.GPT_4O,
        label: 'GPT 4o',
    },
    {
        value: Model.GPT_4O_MINI,
        label: 'GPT 4o mini',
    },
    // Open source models
    {
        value: Model.GPT_OSS_120B,
        label: 'GPT OSS 120B',
    },
    {
        value: Model.GPT_OSS_20B,
        label: 'GPT OSS 20B',
    },
    // Gemini models
    {
        value: Model.GEMINI_3_5_FLASH_LITE,
        label: 'Gemini 3.5 Flash-Lite',
    },
    {
        value: Model.GEMINI_3_5_FLASH,
        label: 'Gemini 3.5 Flash',
    },
    {
        value: Model.GEMINI_3_FLASH_PREVIEW,
        label: 'Gemini 3 Flash',
    },
    // DeepSeek (via OpenRouter)
    {
        value: Model.DEEPSEEK_V4_1_FLASH,
        label: 'DeepSeek V4.1 Flash',
    },
    {
        value: Model.DEEPSEEK_V4_1_FLASH_1017,
        label: 'DeepSeek V4.1 Flash 1017',
    },
    // Claude models
    {
        value: Model.CLAUDE_FABLE_5,
        label: 'Claude Fable 5',
    },
    {
        value: Model.CLAUDE_OPUS_5_5,
        label: 'Claude Opus 5.5',
    },
    {
        value: Model.CLAUDE_OPUS_4_8,
        label: 'Claude Opus 4.8',
    },
    {
        value: Model.CLAUDE_SONNET_5_5,
        label: 'Claude Sonnet 5.5',
    },
    {
        value: Model.CLAUDE_SONNET_5,
        label: 'Claude Sonnet 5',
    },
    {
        value: Model.CLAUDE_SONNET_4_6,
        label: 'Claude Sonnet 4.6',
    },
];

export const DEFAULT_CHAT_MODEL: ModelOption =
    AVAILABLE_MODELS.find((m) => m.value === Model.GPT_4_1_MINI) || AVAILABLE_MODELS[0];

export const AVAILABLE_CODEO_MODELS: ModelOption[] = [
    { value: Model.CLAUDE_FABLE_5, label: 'Claude Fable 5' },
    { value: Model.CLAUDE_OPUS_5_5, label: 'Claude Opus 5.5' },
    { value: Model.CLAUDE_OPUS_4_8, label: 'Claude Opus 4.8' },
    { value: Model.CLAUDE_SONNET_5_5, label: 'Claude Sonnet 5.5' },
    { value: Model.CLAUDE_SONNET_5, label: 'Claude Sonnet 5' },
    { value: Model.CLAUDE_SONNET_4_6, label: 'Claude Sonnet 4.6' },
    { value: Model.CLAUDE_HAIKU_4_5, label: 'Claude Haiku 4.5' },
    { value: Model.GPT_6_1_SOL, label: 'GPT 6.1 Sol' },
    { value: Model.GPT_6_SOL, label: 'GPT 6 Sol' },
    { value: Model.GPT_5_6_SOL, label: 'GPT 5.6 Sol' },
    { value: Model.GPT_5_5, label: 'GPT 5.5' },
    { value: Model.GPT_5_4, label: 'GPT 5.4' },
    { value: Model.GPT_5_4_MINI, label: 'GPT 5.4 mini' },
    { value: Model.GEMINI_3_5_FLASH_LITE, label: 'Gemini 3.5 Flash-Lite' },
    { value: Model.GEMINI_3_5_FLASH, label: 'Gemini 3.5 Flash' },
    { value: Model.GEMINI_3_FLASH_PREVIEW, label: 'Gemini 3 Flash' },
];

// Each vendor has its own reasoning-effort vocabulary and rejects the request
// outright on a value it doesn't define, so callers must send a level the target
// model understands. NONE and MINIMAL are the two vendors' reasoning-off.
export enum ReasoningEffort {
    NONE = 'none',
    MINIMAL = 'minimal',
    LOW = 'low',
    MEDIUM = 'medium',
    HIGH = 'high',
    XHIGH = 'xhigh',
}

export interface ReasoningEffortOption {
    value: ReasoningEffort;
    label: string;
}

// The first entry is that model's reasoning-off.
const GPT5_REASONING_EFFORT_OPTIONS: ReasoningEffortOption[] = [
    { value: ReasoningEffort.NONE, label: 'none' },
    { value: ReasoningEffort.LOW, label: 'low' },
    { value: ReasoningEffort.MEDIUM, label: 'medium' },
    { value: ReasoningEffort.HIGH, label: 'high' },
    { value: ReasoningEffort.XHIGH, label: 'xhigh' },
];

// For models that can't turn reasoning off, the lowest level stands in as the default.
const ALWAYS_REASONING_EFFORT_OPTIONS: ReasoningEffortOption[] =
    GPT5_REASONING_EFFORT_OPTIONS.filter((option) => option.value !== ReasoningEffort.NONE);

// Gemini 3.x thinking_level.
const GEMINI_3_REASONING_EFFORT_OPTIONS: ReasoningEffortOption[] = [
    { value: ReasoningEffort.MINIMAL, label: 'minimal' },
    { value: ReasoningEffort.LOW, label: 'low' },
    { value: ReasoningEffort.MEDIUM, label: 'medium' },
    { value: ReasoningEffort.HIGH, label: 'high' },
];

// OpenRouter's unified `reasoning` param: `none` disables thinking, low/medium/high map to effort.
// Defaults to medium (see defaultReasoningEffortForModel): thinking-on is the more useful default
// for these models; `none` stays available to turn it off.
const OPENROUTER_REASONING_EFFORT_OPTIONS: ReasoningEffortOption[] = [
    { value: ReasoningEffort.NONE, label: 'none' },
    { value: ReasoningEffort.LOW, label: 'low' },
    { value: ReasoningEffort.MEDIUM, label: 'medium' },
    { value: ReasoningEffort.HIGH, label: 'high' },
];

// A model routed through OpenRouter: a vendor-prefixed slug (`vendor/model`) that isn't one of our
// natively-routed prefixes (`google/` = Vertex, `openai/` = Groq's gpt-oss). Pattern-based so new
// OpenRouter models work with no maintained list. OpenRouter normalizes the `reasoning` param per
// model and ignores it for ones with no thinking mode, so exposing the picker for all is safe.
export function isOpenRouterModel(model: string): boolean {
    return model.includes('/') && !model.startsWith('google/') && !model.startsWith('openai/');
}

/**
 * OpenAI's first-party reasoning families (gpt-5.x and gpt-6.x). They share one set of
 * Responses-API behaviours — reasoning routes through /v1/responses, encrypted reasoning
 * items are threaded back across tool calls, server-side compaction applies, and Chat
 * Completions rejects a non-'none' reasoning_effort alongside tools — so callers gate on
 * the family rather than on individual model ids.
 */
export function isOpenAIReasoningModel(model: string): boolean {
    return model.startsWith('gpt-5') || model.startsWith('gpt-6');
}

const ALWAYS_REASONING_MODELS: ReadonlySet<string> = new Set([Model.GPT_6_1_SOL]);

/**
 * OpenAI reasoning models that reject reasoning_effort 'none' and don't support function tools
 * on Chat Completions, so every request has to go through the Responses API with reasoning on.
 */
export function isAlwaysReasoningModel(model: string): boolean {
    return ALWAYS_REASONING_MODELS.has(model);
}

/**
 * The reasoning-effort levels the given model accepts. Empty when the model has no
 * selectable reasoning level, which the UI uses to hide the picker entirely.
 */
export function reasoningEffortOptionsForModel(model: string): ReasoningEffortOption[] {
    if (isAlwaysReasoningModel(model)) {
        return ALWAYS_REASONING_EFFORT_OPTIONS;
    }
    if (isOpenAIReasoningModel(model)) {
        return GPT5_REASONING_EFFORT_OPTIONS;
    }
    if (model.startsWith('google/') && model.includes('gemini-3')) {
        return GEMINI_3_REASONING_EFFORT_OPTIONS;
    }
    if (isOpenRouterModel(model)) {
        return OPENROUTER_REASONING_EFFORT_OPTIONS;
    }
    return [];
}

/** The default reasoning level for a model — normally its reasoning-off (first) option, but
 * medium for OpenRouter models, which are better off thinking by default. */
export function defaultReasoningEffortForModel(model: string): ReasoningEffort | undefined {
    if (isOpenRouterModel(model)) {
        return ReasoningEffort.MEDIUM;
    }
    return reasoningEffortOptionsForModel(model)[0]?.value;
}

// Re-typed from the plain-string values in toolbox-defaults.ts (see that file for why they
// aren't defined as enum members directly) so callers keep getting the nominal enum types.
export const DEFAULT_TOOLBOX_MODEL_VALUE = DEFAULT_TOOLBOX_MODEL_VALUE_STRING as Model.GPT_5_6_LUNA;
export const DEFAULT_TOOLBOX_REASONING_EFFORT =
    DEFAULT_TOOLBOX_REASONING_EFFORT_STRING as ReasoningEffort.MEDIUM;

const TOOLBOX_MODEL_BY_RECIPE: Partial<Record<string, Model>> = {
    [SKILL_NAMES.createMotionGraphicsVideo]: Model.DEEPSEEK_V4_1_FLASH_1017,
    [SKILL_NAMES.createQuickMotionGraphicsVideo]: Model.DEEPSEEK_V4_1_FLASH_1017,
    [SKILL_NAMES.createKineticTypographyVideo]: Model.DEEPSEEK_V4_1_FLASH_1017,
};

export function getToolboxModelForRecipe(recipeName: string | null | undefined): Model | undefined {
    return recipeName ? TOOLBOX_MODEL_BY_RECIPE[recipeName] : undefined;
}

// Model knowledge cutoff dates (based on OpenAI documentation)
// Reference: https://platform.openai.com/docs/models
export const MODEL_KNOWLEDGE_CUTOFFS: Record<Model, string> & { default: string } = {
    [Model.GPT_4_1_MINI]: 'June 01, 2024',
    [Model.GPT_4_1_NANO]: 'June 01, 2024',
    [Model.GPT_4_1]: 'June 01, 2024',
    [Model.GPT_4O]: 'October 01, 2023',
    [Model.GPT_4O_MINI]: 'October 01, 2023',
    [Model.GPT_6_1_SOL]: 'April 30, 2026',
    [Model.GPT_6_SOL]: 'April 20, 2026',
    [Model.GPT_6_LUNA]: 'May 18, 2026',
    [Model.GPT_5_6_SOL]: 'February 16, 2026',
    [Model.GPT_5_6_TERRA]: 'February 16, 2026',
    [Model.GPT_5_6_LUNA]: 'February 16, 2026',
    [Model.GPT_5_5]: 'December 01, 2025',
    [Model.GPT_5_4]: 'August 31, 2025',
    [Model.GPT_5_4_MINI]: 'August 31, 2025',
    [Model.GPT_5_4_NANO]: 'August 31, 2025',
    [Model.GPT_5_2]: 'August 31, 2025',
    [Model.GPT_5_1]: 'September 30, 2024',
    [Model.GPT_OSS_20B]: 'June 01, 2024',
    [Model.GPT_OSS_120B]: 'June 01, 2024',
    [Model.GEMINI_3_FLASH_PREVIEW]: 'January 01, 2025',
    [Model.GEMINI_3_5_FLASH]: 'January 01, 2025',
    [Model.GEMINI_3_5_FLASH_LITE]: 'January 01, 2025',
    [Model.GEMINI_3_8_FLASH]: 'January 01, 2025',
    [Model.INKLING_SMALL]: 'Unknown',
    [Model.QWEN_3_7_PLUS]: 'Unknown',
    [Model.GROK_4_6]: 'Unknown',
    [Model.KIMI_K2_6]: 'Unknown',
    [Model.DEEPSEEK_V4_1_FLASH]: 'Unknown',
    [Model.DEEPSEEK_V4_1_FLASH_1017]: 'Unknown',
    [Model.CLAUDE_FABLE_5]: 'January 01, 2026',
    [Model.CLAUDE_OPUS_5_5]: 'June 01, 2026',
    [Model.CLAUDE_OPUS_4_8]: 'January 01, 2026',
    [Model.CLAUDE_SONNET_5_5]: 'Unknown',
    [Model.CLAUDE_SONNET_5]: 'January 01, 2026',
    [Model.CLAUDE_SONNET_4_6]: 'March 01, 2025',
    [Model.CLAUDE_HAIKU_4_5]: 'March 01, 2025',
    // Default for unknown models
    default: 'Unknown',
};

/**
 * Get the knowledge cutoff date for a given model
 */
export function getKnowledgeCutoff(modelId: string): string {
    return MODEL_KNOWLEDGE_CUTOFFS[modelId as Model] || MODEL_KNOWLEDGE_CUTOFFS['default'];
}

// Model context window limits (safe limits at ~80% of actual context windows)
// Used for automatic conversation truncation to prevent context_length_exceeded errors
export const MODEL_CONTEXT_LIMITS: Record<string, number> = {
    // GPT-4.x family (128K context)
    [Model.GPT_4_1_MINI]: 100_000,
    [Model.GPT_4_1_NANO]: 100_000,
    [Model.GPT_4_1]: 100_000,
    [Model.GPT_4O]: 100_000,
    [Model.GPT_4O_MINI]: 100_000,
    // GPT-5.1 (128K context)
    [Model.GPT_5_1]: 100_000,
    // GPT-5.2+ family (200K context)
    [Model.GPT_5_2]: 160_000,
    [Model.GPT_5_4]: 160_000,
    [Model.GPT_5_4_MINI]: 160_000,
    [Model.GPT_5_4_NANO]: 160_000,
    // GPT-6.x and GPT-5.6 families. The models accept 1.05M tokens, but any request over 272K input
    // tokens reprices the ENTIRE request at long-context rates (~2x), so we cap below that cliff
    // instead of at ~80% of the true window.
    [Model.GPT_6_1_SOL]: 270_000,
    [Model.GPT_6_SOL]: 270_000,
    [Model.GPT_6_LUNA]: 270_000,
    [Model.GPT_5_6_SOL]: 270_000,
    [Model.GPT_5_6_TERRA]: 270_000,
    [Model.GPT_5_6_LUNA]: 270_000,
    // GPT-5.5 (1M+ context)
    [Model.GPT_5_5]: 800_000,
    // GPT-OSS models
    [Model.GPT_OSS_20B]: 100_000,
    [Model.GPT_OSS_120B]: 100_000,
    // Gemini models (1M context)
    [Model.GEMINI_3_FLASH_PREVIEW]: 800_000,
    [Model.GEMINI_3_5_FLASH]: 800_000,
    [Model.GEMINI_3_5_FLASH_LITE]: 800_000,
    [Model.GEMINI_3_8_FLASH]: 800_000,
    // DeepSeek via OpenRouter (1M context)
    [Model.DEEPSEEK_V4_1_FLASH]: 800_000,
    [Model.DEEPSEEK_V4_1_FLASH_1017]: 800_000,
    // Claude models. Fable 5 supports a 1M context, but we cap it to match Opus.
    [Model.CLAUDE_FABLE_5]: 160_000,
    [Model.CLAUDE_OPUS_5_5]: 160_000,
    [Model.CLAUDE_OPUS_4_8]: 160_000,
    [Model.CLAUDE_SONNET_5_5]: 160_000,
    [Model.CLAUDE_SONNET_5]: 160_000,
    [Model.CLAUDE_SONNET_4_6]: 160_000,
    [Model.CLAUDE_HAIKU_4_5]: 160_000,
};

const DEFAULT_CONTEXT_LIMIT = 100_000;

/**
 * Get the context token limit for a given model
 */
export function getContextLimit(modelId: string): number {
    return MODEL_CONTEXT_LIMITS[modelId] || DEFAULT_CONTEXT_LIMIT;
}

// Models that support Anthropic adaptive thinking (`thinking: {type:'adaptive'}`)
// and its summarized reasoning stream. Opus 4.6+, Sonnet 4.6, and Fable 5 support
// it; Haiku 4.5, older Claude models, and non-Claude models do not.
const ADAPTIVE_THINKING_MODELS: ReadonlySet<string> = new Set([
    Model.CLAUDE_FABLE_5,
    Model.CLAUDE_OPUS_5_5,
    Model.CLAUDE_OPUS_4_8,
    Model.CLAUDE_SONNET_5_5,
    Model.CLAUDE_SONNET_5,
    Model.CLAUDE_SONNET_4_6,
]);

/**
 * Whether a model accepts the Anthropic adaptive-thinking parameter. Callers
 * that enable thinking must gate on this so unsupported models (e.g. Haiku 4.5)
 * don't receive a config they would reject.
 */
export function modelSupportsAdaptiveThinking(modelId: string): boolean {
    return ADAPTIVE_THINKING_MODELS.has(modelId);
}

/**
 * Get the current date formatted for display in system prompts
 * Format: "January 10, 2025"
 */
export function getCurrentDate(): string {
    const now = new Date();
    return now.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

export interface ToolDefinition {
    type: 'function';
    function: {
        name: string;
        description: string;
        parameters: {
            type: 'object';

            properties: Record<string, any>;
            required?: string[];
        };
    };
}

export interface ToolExecutionContext {
    parentActionGroup: string;
    toolCallId: string;
    conversationId?: string;
    stopTool?: string;
    abortSignal?: AbortSignal;
    abortController?: AbortController;
    task?: string;
    videoStateManager?: VideoStateManager;
    /** The conversation's model id, so tools can size their output against its context window. */
    model?: string;
    /** Set on a subagent running alongside others: the scene groups it may edit. */
    subagentScope?: SubagentScope;
    /** Set on any subagent: the short label it was started with, shown to the user when it asks. */
    subagentDescription?: string;
    /**
     * Set on an agent whose checks are scoped to its own edits: what it has changed since the
     * user's latest message, so lint and review ask it to fix only what that change caused.
     */
    turnEdits?: TurnEdits;
}

export enum CustomRendererMode {
    /** Shows custom UI only when tool name is clicked (inside collapse) */
    INLINE_HIDDEN = 'inline_hidden',
    /** Shows tool name + spinner, with custom UI always visible below */
    INLINE = 'inline',
    /** Custom renderer controls the full display (no tool name/spinner) */
    FULL = 'full',
}

export type ToolResult = string | (TextContent | ImageContent)[];

export interface EnhancedToolResult {
    result: string;
    /** Multipart content (text + images) to send as the tool message content to the LLM.
     *  When set, this is used as the tool message content instead of `result`.
     *  `result` is still used for display purposes (custom renderers, logging). */
    resultContent?: (TextContent | ImageContent)[];

    injectAsUserMessage?: any;
    /**
     * Video data to attach a "View video state" link for, when the video the tool describes is NOT
     * the one being edited — a reference video. Without this a reference exists in the transcript
     * only as its text annotation, so nothing can be seen or compared when debugging a build that
     * imitated it. Uploaded fire-and-forget, exactly like the turn's own before/after snapshots.
     */
    videoDataSnapshot?: unknown;
    /** Label shown on the snapshot's "View video state" link, e.g. "Reference video". */
    videoDataSnapshotLabel?: string;
    isSuccess?: boolean;
    // When true, the conversation loop stops after this tool without making another LLM call
    stopConversation?: boolean;
    /** Kept on the tool message for the conversation record; no model is ever sent it. */
    record?: Record<string, unknown>;
}

export enum MessageRole {
    SYSTEM = 'system',
    USER = 'user',
    ASSISTANT = 'assistant',
    TOOL = 'tool',
}

export interface ImageContent {
    type: 'image_url';
    image_url: {
        url: string;
    };
}

export interface TextContent {
    type: 'text';
    text: string;
}

export type MessageContent = string | (TextContent | ImageContent)[];

/**
 * How a display-only message is shown. Display-only messages are staff-only debugging
 * annotations by default; a variant makes one visible to end users as well.
 * - `user-facing`: rendered like an ordinary message (e.g. a user message kept in the
 *   transcript but withheld from the model).
 * - `user-facing-error`: rendered as a red notice, optionally with a `displayAction` button.
 */
export type DisplayVariant = 'user-facing' | 'user-facing-error';

export interface DisplayAction {
    label: string;
    href: string;
}

export interface BaseMessage {
    id?: string;
    role: MessageRole;
    content: MessageContent;
    isContextMessage?: boolean;
    isDisplayOnly?: boolean;
    displayVariant?: DisplayVariant;
    displayAction?: DisplayAction;
    isVideoStateUpdateMessage?: boolean;
}

export interface SystemMessage extends BaseMessage {
    role: MessageRole.SYSTEM;
    content: string;
}

export const isSystemMessage = (message: ConversationMessage): message is SystemMessage => {
    return message.role === MessageRole.SYSTEM;
};

export interface UserMessage extends BaseMessage {
    role: MessageRole.USER;
    content: MessageContent;
}

export const isUserMessage = (message: ConversationMessage): message is UserMessage => {
    return message.role === MessageRole.USER;
};

export interface CodeInterpreterFile {
    file_id: string;
    filename: string;
    mime_type?: string;
}

export interface CodeInterpreterOutput {
    container_id: string;
    code?: string;
    logs?: string;
    files?: CodeInterpreterFile[];
}

/** A summarized extended-thinking block returned by Anthropic models. */
export interface ThinkingBlock {
    type: 'thinking';
    thinking: string;
    /** Opaque signature that must be replayed unmodified on later turns. */
    signature: string;
}

/** A redacted thinking block (content withheld but must still be replayed). */
export interface RedactedThinkingBlock {
    type: 'redacted_thinking';
    data: string;
}

export type ReasoningBlock = ThinkingBlock | RedactedThinkingBlock;

export interface AssistantMessage extends BaseMessage {
    role: MessageRole.ASSISTANT;
    content: MessageContent;
    tool_calls?: ToolCall[];
    usage?: Usage;
    code_interpreter_outputs?: CodeInterpreterOutput[];
    openai_response_id?: string;
    /**
     * Raw thinking blocks from the assistant turn. Must be replayed unmodified
     * (Anthropic rejects a turn that drops the thinking blocks preceding a
     * tool_use), so these are sent back verbatim by getMessagesForAPI().
     */
    thinking_blocks?: ReasoningBlock[];
    /**
     * Raw `reasoning` items from a Responses-API turn (with their ids and
     * encrypted_content), captured verbatim. On the Responses API a reasoning
     * model needs its own prior reasoning threaded back between tool calls or it
     * loses its chain-of-thought and loops; getMessagesForAPI() sends these back
     * unmodified. The Chat Completions API never returns reasoning content, so
     * this is always empty there.
     */
    reasoning_items?: Array<Record<string, unknown>>;
    /**
     * Raw `compaction` items from a Responses-API turn, captured verbatim. The
     * server emits one when the conversation crosses the compaction threshold:
     * an opaque, encrypted stand-in for every input item before it. On later
     * requests getMessagesForAPI() sends this item followed only by the
     * messages after it; the earlier messages stay stored for display.
     */
    compaction_items?: Array<Record<string, unknown>>;
    /**
     * Human-readable reasoning summary for display. Updated live while the
     * model thinks; not sent back to the API (replay uses thinking_blocks).
     */
    reasoning?: string;
}

export const isAssistantMessage = (message: ConversationMessage): message is AssistantMessage => {
    return message.role === MessageRole.ASSISTANT;
};

export interface ToolCall {
    id: string;
    type: 'function';
    function: {
        name: string;
        arguments: string;
    };
    /**
     * Vendor passthrough on the tool call. Gemini 3.x puts a `thought_signature`
     * here and requires it back verbatim on the next turn, or tool calling
     * degrades — so this must survive both parsing and replay untouched.
     */
    extra_content?: Record<string, unknown>;
}

export interface ToolMessage extends BaseMessage {
    role: MessageRole.TOOL;
    content: ToolResult;
    tool_call_id: string;
    /** Multipart content (text + images) sent to the LLM in place of `content`.
     *  When set, `getMessagesForAPI()` uses this instead of `content`.
     *  `content` remains the text-only string for display/custom renderers. */
    apiContent?: (TextContent | ImageContent)[];
    isSuccess?: boolean;
    child_conversation_ids?: string[]; // IDs of child conversations spawned by this tool call
    artifact_task_id?: string; // Task ID for viewing oracle artifact in admin
    usage?: Usage; // tool execution metrics (latency tracked here)
    /** The tool's `record`: saved with the conversation, left out of every LLM request. */
    record?: Record<string, unknown>;
}

export const isToolMessage = (message: ConversationMessage): message is ToolMessage => {
    return message.role === MessageRole.TOOL;
};

export type ConversationMessage = SystemMessage | UserMessage | AssistantMessage | ToolMessage;

export type CustomRendererComponentProps<TArgs = unknown, TResultData = unknown> = {
    isLoading: boolean;
    /** The raw tool result text, as the model saw it. */
    result: string;
    toolCall: ToolCall | null;
    /**
     * The call's arguments, validated against the tool's schema exactly as `execute` received
     * them. Null when they don't parse, e.g. a call the model got wrong or one saved under an
     * older schema.
     */
    args: TArgs | null;
    /** What the tool's `parseResultData` made of `result`; null when it has none, e.g. an error. */
    resultData: TResultData | null;
    debugDetails?: React.ReactNode;
};

/**
 * A tool's custom renderer, typed by that tool's args and result data. Written as a method
 * signature so its props are checked bivariantly: that keeps a tool with a specific renderer
 * assignable to a plain `Tool`, which is how conversations hold their tools.
 */
export type CustomRenderer<TArgs = unknown, TResultData = unknown> = {
    render(
        props: CustomRendererComponentProps<TArgs, TResultData>,
    ): ReturnType<React.FunctionComponent>;
}['render'];
